import { apiRequest } from "@/lib/api/http";
import { costDate, costNullableText, costNumber, costText, oneOf } from "@/lib/api/cost-fields";
import { ACTIVITY_EVENT_TYPES, ACTIVITY_RESOURCES } from "@/lib/activity/event-types";
import type {
  ActivityEvent,
  ActivityEventType,
  ActivityResource,
  ActivityUserSummary,
} from "@/types/fleet";

/**
 * Reading the activity log. Writing goes through `lib/activity/emit-store.ts` instead — it batches
 * and must never throw, so it does not share `apiRequest`'s error path.
 */

/** Upstream's default page size. Matches `limit` in `docs/ACTIVITY_API.md`. */
export const ACTIVITY_PAGE_SIZE = 200;

interface RemoteActivityEvent {
  id: string;
  user_id?: string;
  user_name?: string;
  event_type?: string;
  resource?: string;
  resource_id?: string | null;
  label?: string;
  path?: string;
  detail?: Record<string, unknown> | null;
  ip_address?: string;
  user_agent?: string;
  occurred_at?: string;
}

interface RemoteActivitySummary {
  user_id: string;
  user_name?: string;
  username?: string;
  last_login_at?: string | null;
  last_activity_at?: string | null;
  login_count?: string;
  event_count?: string;
}

interface ActivityListResponse {
  success: true;
  data: RemoteActivityEvent[];
  /** Rows matching the filter before `limit`/`offset`, so the page can say "200 of 4813". */
  total?: string;
}

interface SummaryResponse {
  success: true;
  data: RemoteActivitySummary[];
}

export function mapActivityEvent(event: RemoteActivityEvent): ActivityEvent {
  const detail: Record<string, string> = {};
  for (const [key, value] of Object.entries(event.detail ?? {})) {
    if (value === null || value === undefined) continue;
    detail[key] = String(value);
  }

  return {
    id: event.id,
    userId: costText(event.user_id),
    userName: costText(event.user_name),
    eventType: oneOf(event.event_type, ACTIVITY_EVENT_TYPES, "page_view"),
    resource: oneOf(event.resource, ACTIVITY_RESOURCES, "page"),
    resourceId: costNullableText(event.resource_id),
    label: costText(event.label),
    path: costText(event.path),
    detail,
    ipAddress: costText(event.ip_address),
    userAgent: costText(event.user_agent),
    occurredAt: costDate(event.occurred_at),
  };
}

function mapActivitySummary(row: RemoteActivitySummary): ActivityUserSummary {
  return {
    userId: costText(row.user_id),
    userName: costText(row.user_name),
    username: costText(row.username),
    lastLoginAt: costDate(row.last_login_at),
    lastActivityAt: costDate(row.last_activity_at),
    loginCount: costNumber(row.login_count),
    eventCount: costNumber(row.event_count),
  };
}

/**
 * The filters `/api/activity` forwards upstream. The log is the fastest-growing table in the
 * product, so the page always sends at least a date range and a limit.
 */
export interface ActivityFilters {
  /** `YYYY-MM-DD`, inclusive. */
  dateFrom?: string;
  dateTo?: string;
  userIds?: string[];
  eventTypes?: ActivityEventType[];
  resources?: ActivityResource[];
  /** Free-text match over `label` and `path`, applied upstream. */
  search?: string;
  limit?: number;
  offset?: number;
}

/** Only the keys the proxy handler allows through; anything else it drops before upstream sees it. */
export function activityFiltersToQuery(filters: ActivityFilters): string {
  const params = new URLSearchParams();
  if (filters.dateFrom) params.set("date_from", filters.dateFrom);
  if (filters.dateTo) params.set("date_to", filters.dateTo);
  if (filters.userIds?.length) params.set("user_id", filters.userIds.join(","));
  if (filters.eventTypes?.length) params.set("event_type", filters.eventTypes.join(","));
  if (filters.resources?.length) params.set("resource", filters.resources.join(","));
  if (filters.search?.trim()) params.set("q", filters.search.trim());
  params.set("limit", String(filters.limit ?? ACTIVITY_PAGE_SIZE));
  if (filters.offset) params.set("offset", String(filters.offset));
  return `?${params.toString()}`;
}

export interface ActivityPage {
  events: ActivityEvent[];
  /** Total matching the filter upstream, which may exceed `events.length`. */
  total: number;
}

export async function listActivity(filters: ActivityFilters = {}): Promise<ActivityPage> {
  const response = await apiRequest<ActivityListResponse>(
    `/api/activity${activityFiltersToQuery(filters)}`,
    { method: "GET", cache: "no-store" },
  );
  const events = response.data.map(mapActivityEvent);
  // Upstream omits `total` when it has nothing to page; the list length is then the truth.
  return { events, total: response.total === undefined ? events.length : costNumber(response.total) };
}

export async function listActivitySummary(
  filters: Pick<ActivityFilters, "dateFrom" | "dateTo"> = {},
): Promise<ActivityUserSummary[]> {
  const params = new URLSearchParams();
  if (filters.dateFrom) params.set("date_from", filters.dateFrom);
  if (filters.dateTo) params.set("date_to", filters.dateTo);
  const query = params.toString();

  const response = await apiRequest<SummaryResponse>(
    `/api/activity/summary${query ? `?${query}` : ""}`,
    { method: "GET", cache: "no-store" },
  );
  return response.data.map(mapActivitySummary);
}
